import { getPacketDedupKey } from "../UseVehicleTelemetry/normalizers";

const parseTimestamp = (value) => {
  if (value === null || value === undefined || value === "") return NaN;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : Date.parse(value);
};

export const updateLatestDeviceDataCache = (cache = {}, data, searchBy) => {
  if (data?.packet_type !== "device_data") return cache;

  const identifier = data?.[searchBy];
  const incomingTimestamp = parseTimestamp(data?.source_time);
  if (identifier === null || identifier === undefined || !Number.isFinite(incomingTimestamp)) {
    return cache;
  }

  const cachedData = cache[identifier];
  const cachedTimestamp = parseTimestamp(cachedData?.source_time);
  if (Number.isFinite(cachedTimestamp) && incomingTimestamp < cachedTimestamp) {
    return cache;
  }

  return {
    ...cache,
    [identifier]: data,
  };
};

export const updateLatestMovementDataCache = (cache = {}, data, searchBy) => {
  const speed = data?.velocity?.speed;
  const hasValidSpeed =
    speed !== null &&
    speed !== undefined &&
    speed !== "" &&
    Number.isFinite(Number(speed));

  if (!hasValidSpeed) return cache;

  return updateLatestDeviceDataCache(cache, data, searchBy);
};
/**
 * Accumulates incoming realtime packets in a cache keyed by plate number
 * (or whichever identifier `searchBy` names), instead of replacing the whole
 * cache object.
 *
 * The previous listener did `setRealTimeData({ [Data?.[searchBy]]: Data })`, so a
 * packet for any other vehicle dropped the selected vehicle's cached update
 * (bugfix.md 1.5 / 2.5). This helper keeps every vehicle's newest packet.
 *
 * Rules:
 * - Any packet type is cached (the realtime cache is generic activity, not just
 *   movement); only a missing identifier is rejected.
 * - An older packet never regresses a newer cached packet for the same key.
 * - Duplicates are ignored: the same `packetId` (fallback
 *   `plateNumber + sourceId + sourceTime + packetType`) returns the cache
 *   unchanged, preserving the existing dedup behavior (bugfix.md 3.8).
 * - The cache is returned by reference when nothing changed, so callers relying
 *   on referential equality do not re-render.
 *
 * @param {Object} cache - Current cache keyed by identifier.
 * @param {Object} data - Incoming packet.
 * @param {string} searchBy - Packet key holding the identifier (e.g. `plate_number`).
 * @returns {Object} The updated cache, or the same cache when unchanged.
 */
export const upsertPacketByPlate = (cache = {}, data, searchBy = "plate_number") => {
  if (!data || typeof data !== "object") return cache;

  const identifier = data?.[searchBy];
  if (identifier === null || identifier === undefined || identifier === "") return cache;

  const cachedData = cache[identifier];

  // Same packet already cached - nothing to do (dedup).
  if (cachedData && getPacketDedupKey(cachedData) === getPacketDedupKey(data)) {
    return cache;
  }

  const incomingTimestamp = parseTimestamp(data?.source_time);
  const cachedTimestamp = parseTimestamp(cachedData?.source_time);

  if (Number.isFinite(cachedTimestamp)) {
    // Keep the newer observation; an unparseable incoming time cannot displace
    // a known one.
    if (!Number.isFinite(incomingTimestamp) || incomingTimestamp < cachedTimestamp) {
      return cache;
    }
  }

  return {
    ...cache,
    [identifier]: data,
  };
};
